import React, { useState, useEffect, useRef } from 'react'
import apiClient, { API_ENDPOINTS } from '../../config/api'

const formatNumber = (value) => {
  const num = Number(value ?? 0)
  if (isNaN(num)) return '0.00'
  return num.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  })
}

const formatCurrency = (value) => `$${formatNumber(value)}`

const formatInteger = (value) => {
  const num = Number(value ?? 0)
  if (isNaN(num)) return '0'
  return Math.round(num).toLocaleString('en-US')
}

const TOP_BAR_GRADIENT = 'linear-gradient(135deg, rgba(19, 56, 181, 0.45), rgba(92, 142, 240, 0.22))'
const BLUE_GLASS_GRADIENT = 'linear-gradient(135deg, rgba(11, 94, 215, 0.5), rgba(98, 179, 255, 0.25))'

const useCountUp = (value, duration = 1200) => {
  const [displayValue, setDisplayValue] = useState(0)

  useEffect(() => {
    const target = Number(value ?? 0)
    let frameId
    let startTimestamp = null

    const animate = timestamp => {
      if (startTimestamp === null) {
        startTimestamp = timestamp
      }
      const progress = Math.min((timestamp - startTimestamp) / duration, 1)
      const currentValue = Math.round(target * progress)
      setDisplayValue(currentValue)
      if (progress < 1) {
        frameId = requestAnimationFrame(animate)
      }
    }

    frameId = requestAnimationFrame(animate)
    return () => cancelAnimationFrame(frameId)
  }, [value, duration])

  return displayValue
}

const MetricCard = ({ title, subtitle, icon, value, footer, gradient = TOP_BAR_GRADIENT, delay = 0, formatValue }) => {
  const animatedValue = useCountUp(value)
  const cardGradient = gradient
  const displayValue = formatValue ? formatValue(animatedValue) : formatNumber(animatedValue)
  
  return (
    <div className="col-xxl-3 col-lg-3 col-md-6 col-sm-6">
      <div
        className="card border-0 text-white position-relative overflow-hidden metric-card metric-card-animate"
        style={{
          minHeight: 120,
          borderRadius: 12,
          background: 'rgba(15, 23, 42, 0.55)',
          backdropFilter: 'blur(10px)',
          WebkitBackdropFilter: 'blur(10px)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          boxShadow: '0 8px 16px rgba(15, 23, 42, 0.25)',
          '--metric-card-delay': `${delay}s`
        }}
      >
        <div
          style={{
            position: 'absolute',
            inset: 0,
            background: cardGradient,
            opacity: 0.75
          }}
        />
        <div className="card-body position-relative p-3 d-flex flex-column gap-2">
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <p className="text-uppercase mb-0 text-white-50" style={{ fontSize: '0.65rem', letterSpacing: '0.08em' }}>{subtitle}</p>
              <span className="fw-semibold" style={{ fontSize: '0.9rem' }}>{title}</span>
            </div>
            <div
              className="rounded-circle d-flex align-items-center justify-content-center"
              style={{
                width: 32,
                height: 32,
                background: 'rgba(255, 255, 255, 0.12)',
                border: '1px solid rgba(255,255,255,0.2)'
              }}
            >
              <i className={`${icon} fs-6`}></i>
            </div>
          </div>
          <div>
            <h5 className="fw-bold mb-1 count-up-number" style={{ fontSize: '1.25rem' }}>{displayValue}</h5>
            {footer && <p className="mb-0" style={{ fontSize: '0.75rem', opacity: 0.7 }}>{footer}</p>}
          </div>
        </div>
      </div>
    </div>
  )
}

const Dashboard = () => {
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [data, setData] = useState(null)

  // Chart refs
  const stockByCategoryChartRef = useRef(null)
  const topProductsChartRef = useRef(null)
  const costVsSellingChartRef = useRef(null)

  useEffect(() => {
    const fetchDashboardData = async () => {
      setLoading(true)
      setError(null)
      try {
        const response = await apiClient.get(API_ENDPOINTS.BRANCH_DASHBOARD)
        setData(response.data)
      } catch (err) {
        console.error('Error fetching dashboard data:', err)
        setError('Failed to load dashboard data. Please try again later.')
      } finally {
        setLoading(false)
      }
    }

    fetchDashboardData()
  }, [])

  // Initialize charts when data is available
  useEffect(() => {
    if (!data || !window.ApexCharts) return

    const destroyCharts = () => {
      const refs = [stockByCategoryChartRef, topProductsChartRef, costVsSellingChartRef]
      refs.forEach(ref => {
        if (ref.current?.chart) {
          ref.current.chart.destroy()
          ref.current.chart = null
        }
      })
    }

    destroyCharts()

    // Stock by Category - Donut Chart
    if (data.stock_by_category?.length && stockByCategoryChartRef.current) {
      const chart = new window.ApexCharts(stockByCategoryChartRef.current, {
        series: data.stock_by_category.map(item => {
          const cost = Number(item.total_cost ?? 0)
          return isNaN(cost) ? 0 : cost
        }),
        chart: {
          type: 'donut',
          height: 350,
          toolbar: { show: true }
        },
        labels: data.stock_by_category.map(item => item.category_name),
        tooltip: {
          y: {
            formatter: (val) => formatCurrency(val)
          }
        },
        plotOptions: {
          pie: {
            donut: {
              size: '60%',
              labels: {
                show: true,
                name: {
                  show: true,
                  fontSize: '16px',
                  fontWeight: 600
                },
                value: {
                  show: true,
                  fontSize: '14px',
                  fontWeight: 400,
                  formatter: (val) => formatCurrency(val)
                },
                total: {
                  show: true,
                  label: 'Total Cost',
                  formatter: () => formatCurrency(
                    data.stock_by_category.reduce((sum, item) => sum + (Number(item.total_cost ?? 0) || 0), 0)
                  )
                }
              }
            }
          }
        },
        colors: ['#4154f1', '#2eca6a', '#ff771d', '#f72585', '#7209b7', '#3a0ca3', '#4361ee', '#4cc9f0'],
        legend: {
          position: 'bottom',
          horizontalAlign: 'center'
        }
      })
      chart.render()
      stockByCategoryChartRef.current.chart = chart
    }

    // Top Products by Value - Horizontal Bar Chart
    if (data.top_products_by_value?.length && topProductsChartRef.current) {
      const chart = new window.ApexCharts(topProductsChartRef.current, {
        series: [{
          name: 'Total Cost',
          data: data.top_products_by_value.map(item => {
            const cost = Number(item.total_cost ?? 0)
            return isNaN(cost) ? 0 : cost
          })
        }],
        chart: {
          type: 'bar',
          height: 350,
          toolbar: { show: true }
        },
        plotOptions: {
          bar: {
            horizontal: true,
            borderRadius: 4
          }
        },
        xaxis: {
          categories: data.top_products_by_value.map(item => item.product_name)
        },
        yaxis: {
          labels: {
            formatter: (val) => formatCurrency(val)
          }
        },
        tooltip: {
          y: {
            formatter: (val) => formatCurrency(val)
          }
        },
        colors: ['#4154f1'],
        dataLabels: {
          enabled: true,
          formatter: (val) => formatCurrency(val)
        }
      })
      chart.render()
      topProductsChartRef.current.chart = chart
    }

    // Cost vs Selling Value Comparison - Column Chart
    if (data.stock_by_category?.length && costVsSellingChartRef.current) {
      const chart = new window.ApexCharts(costVsSellingChartRef.current, {
        series: [
          {
            name: 'Total Cost',
            data: data.stock_by_category.map(item => {
              const cost = Number(item.total_cost ?? 0)
              return isNaN(cost) ? 0 : cost
            })
          },
          {
            name: 'Total Selling Value',
            data: data.stock_by_category.map(item => {
              const value = Number(item.total_selling_value ?? 0)
              return isNaN(value) ? 0 : value
            })
          }
        ],
        chart: {
          type: 'bar',
          height: 350,
          toolbar: { show: true }
        },
        plotOptions: {
          bar: {
            horizontal: false,
            columnWidth: '55%',
            endingShape: 'rounded'
          }
        },
        xaxis: {
          categories: data.stock_by_category.map(item => item.category_name)
        },
        yaxis: {
          labels: {
            formatter: (val) => formatCurrency(val)
          }
        },
        tooltip: {
          y: {
            formatter: (val) => formatCurrency(val)
          }
        },
        colors: ['#4154f1', '#2eca6a'],
        fill: { opacity: 1 },
        dataLabels: {
          enabled: false
        }
      })
      chart.render()
      costVsSellingChartRef.current.chart = chart
    }

    return () => {
      destroyCharts()
    }
  }, [data])

  if (loading) {
    return (
      <>
        <div className="pagetitle">
          <h1>Dashboard</h1>
          <nav>
            <ol className="breadcrumb">
              <li className="breadcrumb-item"><a href="/">Home</a></li>
              <li className="breadcrumb-item active">Dashboard</li>
            </ol>
          </nav>
        </div>
        <section className="section">
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading dashboard...</span>
            </div>
          </div>
        </section>
      </>
    )
  }

  if (error) {
    return (
      <>
        <div className="pagetitle">
          <h1>Dashboard</h1>
          <nav>
            <ol className="breadcrumb">
              <li className="breadcrumb-item"><a href="/">Home</a></li>
              <li className="breadcrumb-item active">Dashboard</li>
            </ol>
          </nav>
        </div>
        <section className="section">
          <div className="alert alert-danger d-flex align-items-center" role="alert">
            <i className="bi bi-exclamation-triangle me-2"></i>
            {error}
          </div>
        </section>
      </>
    )
  }

  const stockEval = data?.stock_evaluation || {}
  const branch = data?.branch || {}

  return (
    <>
      <div className="pagetitle">
        <div className="d-flex justify-content-between align-items-center">
          <div>
            <h1>Branch Dashboard</h1>
            <nav>
              <ol className="breadcrumb">
                <li className="breadcrumb-item"><a href="/">Home</a></li>
                <li className="breadcrumb-item active">Dashboard</li>
              </ol>
            </nav>
          </div>
          {branch.name && (
            <div className="text-end">
              <h5 className="mb-0 text-primary">{branch.name}</h5>
              <small className="text-muted">Warehouse: {branch.warehouse_name}</small>
            </div>
          )}
        </div>
      </div>

      <section className="section dashboard">
        {/* Stock Evaluation Summary Cards */}
        <div className="row g-4">
          <MetricCard
            title="Total Stock Entries"
            subtitle="All Time"
            icon="bi bi-box-seam"
            value={stockEval.total_stock_entries || 0}
            footer="Stock entries"
            gradient={TOP_BAR_GRADIENT}
            delay={0}
            formatValue={formatInteger}
          />
          <MetricCard
            title="Total Quantity"
            subtitle="In Stock"
            icon="bi bi-layers"
            value={stockEval.total_quantity || 0}
            footer="Units"
            gradient={TOP_BAR_GRADIENT}
            delay={0.05}
            formatValue={formatInteger}
          />
          <MetricCard
            title="Total Cost Value"
            subtitle="Investment"
            icon="bi bi-currency-dollar"
            value={stockEval.total_cost_value || 0}
            footer="Purchase cost"
            gradient={BLUE_GLASS_GRADIENT}
            delay={0.1}
            formatValue={formatCurrency}
          />
          <MetricCard
            title="Total Selling Value"
            subtitle="Potential"
            icon="bi bi-cash-stack"
            value={stockEval.total_selling_value || 0}
            footer="At selling price"
            gradient={BLUE_GLASS_GRADIENT}
            delay={0.15}
            formatValue={formatCurrency}
          />
        </div>

        {/* Profit and Products Cards */}
        <div className="row g-4 mt-1">
          <MetricCard
            title="Potential Profit"
            subtitle="Revenue"
            icon="bi bi-graph-up-arrow"
            value={stockEval.potential_profit || 0}
            footer={`${Number(stockEval.profit_margin_percentage || 0).toFixed(2)}% Margin`}
            gradient={TOP_BAR_GRADIENT}
            delay={0}
            formatValue={formatCurrency}
          />
          <MetricCard
            title="Profit Margin"
            subtitle="ROI"
            icon="bi bi-percent"
            value={stockEval.profit_margin_percentage || 0}
            footer="Return on investment"
            gradient={BLUE_GLASS_GRADIENT}
            delay={0.05}
            formatValue={(val) => `${Number(val).toFixed(2)}%`}
          />
          <MetricCard
            title="Unique Products"
            subtitle="Catalog"
            icon="bi bi-box-seam"
            value={stockEval.unique_products || 0}
            footer="Product varieties"
            gradient={TOP_BAR_GRADIENT}
            delay={0.1}
            formatValue={formatInteger}
          />
        </div>

        {/* Charts Section */}
        <div className="row g-4 mb-4">
          {/* Stock by Category - Donut Chart */}
          <div className="col-lg-6">
            <div className="card shadow-sm border-0 h-100">
              <div className="card-body d-flex flex-column">
                <h5 className="card-title">
                  <i className="bi bi-pie-chart text-primary me-2"></i>
                  Stock by Category
                </h5>
                {data?.stock_by_category?.length ? (
                  <>
                    <div ref={stockByCategoryChartRef} className="flex-grow-1"></div>
                    <div className="mt-3">
                      <div className="table-responsive">
                        <table className="table table-sm table-hover">
                          <thead className="table-light">
                            <tr>
                              <th>Category</th>
                              <th className="text-end">Quantity</th>
                              <th className="text-end">Cost</th>
                              <th className="text-end">Selling Value</th>
                              <th className="text-end">Products</th>
                            </tr>
                          </thead>
                          <tbody>
                            {data.stock_by_category.map((category, idx) => (
                              <tr key={category.category_id || idx}>
                                <td className="fw-semibold">{category.category_name}</td>
                                <td className="text-end">{formatNumber(category.total_quantity)}</td>
                                <td className="text-end">{formatCurrency(category.total_cost)}</td>
                                <td className="text-end">{formatCurrency(category.total_selling_value)}</td>
                                <td className="text-end">
                                  <span className="badge bg-secondary">{category.product_count}</span>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="text-center py-4 text-muted flex-grow-1 d-flex align-items-center justify-content-center">
                    <div>
                      <i className="bi bi-pie-chart fs-3 mb-2 d-block"></i>
                      <span>No category data available</span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Top Products by Value - Bar Chart */}
          <div className="col-lg-6">
            <div className="card shadow-sm border-0 h-100">
              <div className="card-body d-flex flex-column">
                <h5 className="card-title">
                  <i className="bi bi-bar-chart text-primary me-2"></i>
                  Top Products by Value
                </h5>
                {data?.top_products_by_value?.length ? (
                  <>
                    <div ref={topProductsChartRef} className="flex-grow-1"></div>
                    <div className="mt-3">
                      <div className="table-responsive">
                        <table className="table table-sm table-hover">
                          <thead className="table-light">
                            <tr>
                              <th>Product</th>
                              <th>SKU</th>
                              <th className="text-end">Quantity</th>
                              <th className="text-end">Total Cost</th>
                            </tr>
                          </thead>
                          <tbody>
                            {data.top_products_by_value.map((product, idx) => (
                              <tr key={product.product_id || idx}>
                                <td className="fw-semibold">{product.product_name}</td>
                                <td><span className="badge bg-secondary">{product.product_sku}</span></td>
                                <td className="text-end">{formatNumber(product.total_quantity)}</td>
                                <td className="text-end fw-semibold">{formatCurrency(product.total_cost)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="text-center py-4 text-muted flex-grow-1 d-flex align-items-center justify-content-center">
                    <div>
                      <i className="bi bi-bar-chart fs-3 mb-2 d-block"></i>
                      <span>No product data available</span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Cost vs Selling Value Comparison */}
        {data?.stock_by_category?.length > 0 && (
          <div className="row g-4 mb-4">
            <div className="col-12">
              <div className="card shadow-sm border-0">
                <div className="card-body">
                  <h5 className="card-title">
                    <i className="bi bi-graph-up-arrow text-primary me-2"></i>
                    Cost vs Selling Value by Category
                  </h5>
                  <div ref={costVsSellingChartRef}></div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Low Stock Items */}
        {data?.low_stock_items?.length > 0 && (
          <div className="row g-4 mb-4">
            <div className="col-12">
              <div className="card shadow-sm border-0">
                <div className="card-body">
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h5 className="card-title mb-0">
                      <i className="bi bi-exclamation-triangle text-warning me-2"></i>
                      Low Stock Items
                    </h5>
                    <span className="badge bg-warning">{data.low_stock_items.length} items</span>
                  </div>
                  <div className="table-responsive">
                    <table className="table table-hover">
                      <thead className="table-light">
                        <tr>
                          <th>Product</th>
                          <th>SKU</th>
                          <th>Category</th>
                          <th className="text-end">Current Qty</th>
                          <th className="text-end">Reorder Level</th>
                          <th className="text-end">Purchase Price</th>
                          <th className="text-end">Selling Price</th>
                          <th>Batch Number</th>
                          <th>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.low_stock_items.map((item, idx) => {
                          const isLowStock = (item.current_quantity || 0) < (item.reorder_level || 0)
                          const isOutOfStock = (item.current_quantity || 0) === 0
                          return (
                            <tr key={idx}>
                              <td className="fw-semibold">{item.product_name}</td>
                              <td><span className="badge bg-secondary">{item.product_sku}</span></td>
                              <td>{item.category_name}</td>
                              <td className="text-end fw-semibold">{formatNumber(item.current_quantity)}</td>
                              <td className="text-end">{formatNumber(item.reorder_level)}</td>
                              <td className="text-end">{formatCurrency(item.purchase_price)}</td>
                              <td className="text-end">{formatCurrency(item.selling_price)}</td>
                              <td><small className="text-muted">{item.batch_number}</small></td>
                              <td>
                                {isOutOfStock ? (
                                  <span className="badge bg-danger">Out of Stock</span>
                                ) : isLowStock ? (
                                  <span className="badge bg-warning">Low Stock</span>
                                ) : (
                                  <span className="badge bg-success">OK</span>
                                )}
                              </td>
                            </tr>
                          )
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Recent Stock Additions */}
        <div className="row g-4 mb-4">
          <div className="col-12">
            <div className="card shadow-sm border-0">
              <div className="card-body">
                <h5 className="card-title">
                  <i className="bi bi-clock-history text-primary me-2"></i>
                  Recent Stock Additions
                </h5>
                {data?.recent_stock_additions?.length ? (
                  <div className="table-responsive" style={{ maxHeight: '500px', overflowY: 'auto' }}>
                    <table className="table table-sm table-hover">
                      <thead className="table-light sticky-top">
                        <tr>
                          <th>Product</th>
                          <th>SKU</th>
                          <th className="text-end">Qty</th>
                          <th className="text-end">Purchase</th>
                          <th className="text-end">Selling</th>
                          <th>Date</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.recent_stock_additions.map((item, idx) => {
                          const date = new Date(item.received_date || item.created_at)
                          return (
                            <tr key={idx}>
                              <td className="fw-semibold">{item.product_name}</td>
                              <td><span className="badge bg-secondary">{item.product_sku}</span></td>
                              <td className="text-end">{formatNumber(item.quantity)}</td>
                              <td className="text-end">{formatCurrency(item.purchase_price)}</td>
                              <td className="text-end">{formatCurrency(item.selling_price)}</td>
                              <td>
                                <small className="text-muted">
                                  {date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                                </small>
                              </td>
                            </tr>
                          )
                        })}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-center py-4 text-muted">
                    <i className="bi bi-inbox fs-3 mb-2 d-block"></i>
                    <span>No recent stock additions</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Stock Summary */}
        <div className="row g-4 mb-4">
          <div className="col-12">
            <div className="card shadow-sm border-0">
              <div className="card-body">
                <h5 className="card-title">
                  <i className="bi bi-list-ul text-primary me-2"></i>
                  Stock Summary
                </h5>
                {data?.stock_summary?.length ? (
                  <div className="table-responsive" style={{ maxHeight: '500px', overflowY: 'auto' }}>
                    <table className="table table-sm table-hover">
                      <thead className="table-light sticky-top">
                        <tr>
                          <th>Product</th>
                          <th>SKU</th>
                          <th>Category</th>
                          <th className="text-end">Total Qty</th>
                          <th className="text-end">Total Cost</th>
                          <th className="text-end">Batches</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.stock_summary.map((item, idx) => (
                          <tr key={item.product_id || idx}>
                            <td className="fw-semibold">{item.product_name}</td>
                            <td><span className="badge bg-secondary">{item.product_sku}</span></td>
                            <td>{item.category_name}</td>
                            <td className="text-end fw-semibold">{formatNumber(item.total_quantity)}</td>
                            <td className="text-end">{formatCurrency(item.total_cost)}</td>
                            <td className="text-end">
                              <span className="badge bg-info">{item.batch_count}</span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-center py-4 text-muted">
                    <i className="bi bi-inbox fs-3 mb-2 d-block"></i>
                    <span>No stock summary available</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  )
}

export default Dashboard
